import { supabase } from './supabase';
import { buildPublicApiUrl } from '@shared/lib/publicApiUrl';

const API_BASE = import.meta.env.VITE_API_BASE || '';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  let session = (await supabase.auth.getSession()).data.session;

  if (!session?.access_token) {
    await new Promise((resolve) => setTimeout(resolve, 150));
    session = (await supabase.auth.getSession()).data.session;
  }

  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  return headers;
}

export async function publicGet<T>(path: string): Promise<T> {
  const url = buildPublicApiUrl(API_BASE, path);
  const response = await fetch(url);

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error ?? `Request failed: ${response.status}`);
  }

  return response.json();
}

export async function adminRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers = await getAuthHeaders();
  if (!headers['Authorization']) {
    throw new Error('Not signed in');
  }
  headers['Content-Type'] = 'application/json';

  const url = buildPublicApiUrl(API_BASE, path);
  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error ?? `Request failed: ${response.status}`);
  }

  return response.json();
}

export interface ArticleMediaUploadResult {
  kind: 'image' | 'video';
  url: string;
  posterUrl?: string;
}

export async function adminUploadMedia(file: File): Promise<ArticleMediaUploadResult> {
  const headers = await getAuthHeaders();
  if (!headers['Authorization']) {
    throw new Error('Not signed in');
  }
  const formData = new FormData();
  formData.append('file', file);

  const url = buildPublicApiUrl(API_BASE, '/api/cms/articles/media/upload');
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error ?? `Upload failed: ${response.status}`);
  }

  return response.json();
}
